import React, { useEffect, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { useAuth } from "../contexts/AuthContext";
import { upsertUserToDashboard } from "../lib/authService";
import { supabase } from "../lib/supabase";
import "./AuthPopup.css";

export default function AuthPopup({ onClose, onSuccess }) {
  const [tab, setTab] = useState("signin"); // signin | signup
  const [step, setStep] = useState("form"); // form | forgot | forgot-sent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const modalRef = useRef(null);

  const { signIn, signUp, signInWithGoogle, signInWithOtp } = useAuth();

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      // If clicking the overlay itself (the parent div of the modal), close it.
      if (e.target.classList.contains("authOverlay")) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose]);

  const switchTab = (newTab) => {
    setTab(newTab);
    setStep("form");
    setError("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (err) throw err;
      setStep("forgot-sent");
    } catch (err) {
      setError(err?.message || "Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    setLoading(true);
    try {
      if (tab === "signin") {
        const { data, error: err } = await signIn(email, password);
        if (err) throw err;
        if (data?.user) await upsertUserToDashboard(data.user);
        (onSuccess || onClose)();
      } else {
        const { error: err } = await signInWithOtp(email, {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}/`
        });
        if (err) throw err;
        setStep("signup-sent");
      }
    } catch (err) {
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const { error: err } = await signInWithGoogle();
      if (err) throw err;
      (onSuccess || onClose)();
    } catch (err) {
      setError(err?.message || "Google sign-in failed.");
      setLoading(false);
    }
  };

  return (
    <div className="authOverlay" role="dialog" aria-modal="true">
      <div className="authModal" ref={modalRef}>
        
        {/* Close Button must be visually prominent and technically sound */}
        <button 
          className="authClose" 
          type="button" 
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }} 
          aria-label="Close"
          style={{ cursor: "pointer", zIndex: 9999, pointerEvents: "auto" }}
        >
          <FiX style={{ pointerEvents: "none" }} />
        </button>

        <div className="authHeader">
          <h2>Welcome</h2>
          <p>Keep your Wishlist and Cart in sync on any device.</p>
        </div>

        {step === "form" && (
          <div className="authTabs" role="tablist">
            <button
              type="button"
              className={`authTab ${tab === "signin" ? "active" : ""}`}
              onClick={() => switchTab("signin")}
              role="tab"
              aria-selected={tab === "signin"}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`authTab ${tab === "signup" ? "active" : ""}`}
              onClick={() => switchTab("signup")}
              role="tab"
              aria-selected={tab === "signup"}
            >
              Create Account
            </button>
          </div>
        )}

        {step === "forgot" && (
          <form className="authForm" onSubmit={handleForgotPassword}>
            <h3 style={{ color: "#7a0f1b", margin: "0 0 8px", fontSize: "20px" }}>Reset Password</h3>
            <p style={{ color: "#666", fontSize: "14px", marginBottom: "20px" }}>
              Enter your account email and we'll send you a password reset link.
            </p>
            {error && <p className="authError">{error}</p>}
            <label className="authLabel">Email Address</label>
            <input
              className="authInput"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="authPrimary" type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
            <button type="button" className="authLink" onClick={() => { setStep("form"); setError(""); }}>
              ← Back to Sign In
            </button>
          </form>
        )}

        {step === "forgot-sent" && (
          <div className="authForm" style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{
              width: "60px", height: "60px",
              background: "linear-gradient(135deg, #7a0f1b, #c0392b)",
              borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 16px",
              fontSize: "26px", color: "#fff"
            }}>✉</div>
            <h3 style={{ color: "#7a0f1b", marginBottom: "10px" }}>Check your inbox!</h3>
            <p style={{ color: "#555", fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}>
              We sent a password reset link to:<br />
              <strong style={{ color: "#7a0f1b" }}>{email}</strong>
            </p>
            <p style={{ color: "#888", fontSize: "13px", marginBottom: "24px" }}>
              Click the link in the email to set a new password.
            </p>
            <button type="button" className="authLink" onClick={() => { setStep("form"); setError(""); setEmail(""); }}>
              ← Back to Sign In
            </button>
          </div>
        )}

        {step === "signup-sent" && (
          <div className="authForm" style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{
              width: "60px", height: "60px",
              background: "linear-gradient(135deg, #7a0f1b, #c0392b)",
              borderRadius: "50%", display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 16px",
              fontSize: "26px", color: "#fff"
            }}>✉</div>
            <h3 style={{ color: "#7a0f1b", marginBottom: "10px" }}>Verification Link Sent!</h3>
            <p style={{ color: "#555", fontSize: "14px", lineHeight: "1.6", marginBottom: "8px" }}>
              We sent a secure magic link to:<br />
              <strong style={{ color: "#7a0f1b" }}>{email}</strong>
            </p>
            <p style={{ color: "#888", fontSize: "13px", marginBottom: "24px" }}>
              Click the link to verify your email and securely log in to your account.
            </p>
            <button type="button" className="authLink" onClick={() => { setStep("form"); setError(""); setEmail(""); }}>
              ← Back to Sign In
            </button>
          </div>
        )}

        {step === "form" && (
          <form className="authForm" onSubmit={handleSubmit}>
            {error && <p className="authError">{error}</p>}

            <label className="authLabel">Email Address</label>
            <input
              className="authInput"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {tab === "signin" && (
              <>
                <label className="authLabel">Password</label>
                <input
                  className="authInput"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </>
            )}

            <button className="authPrimary" type="submit" disabled={loading}>
              {loading ? "Please wait..." : tab === "signin" ? "Sign In" : "Send Magic Link to Verify"}
            </button>

            <div className="authDivider"><span>or</span></div>

            <button type="button" className="authSecondary" onClick={handleGoogleSignIn} disabled={loading}>
              <FcGoogle className="googleIcon" />
              Continue with Google
            </button>

            {tab === "signin" && (
              <button
                type="button"
                className="authForgotLink"
                onClick={() => { setStep("forgot"); setError(""); setEmail(""); }}
              >
                Forgot your password?
              </button>
            )}

            {tab === "signin" ? (
              <button type="button" className="authLink" onClick={() => switchTab("signup")}>
                New here? Create account
              </button>
            ) : (
              <button type="button" className="authLink" onClick={() => switchTab("signin")}>
                Already have an account? Sign in
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}